import { useEffect, useRef, useState } from "react";
import { DDZWallet } from "@/utils/DDZWallet";
import { SendCMD } from "@/utils/HTTPRequest";
import { GetHallServerUrl } from "@/data/urls";
import { normalizeMjh5Entry } from "@/config/Mjh5";
import style from "./style.less";

const copy = {
  en: {
    title: "DDZ Wallet", close: "Close", refresh: "Refresh balances", retry: "Check transfer result", amount: "Transfer amount",
    unit: "Enter a whole number in the smallest currency unit shown. No decimals.",
    labels: ["Website balance", "Available for PVP", "DDZ balance", "Available to transfer in", "Available to transfer out"],
    up: "Transfer in", down: "Transfer out", enter: "Enter game", loading: "Loading…", pending: "Processing. Check the existing transfer before starting another.",
    note: "Enter game does not transfer funds. Exit DDZ before transferring out. Bonus funds may be unavailable for PVP.",
    done: "Transfer successful", unavailable: "Balance unavailable. Refresh to try again.",
    errors: {
      INVALID_AMOUNT: "Enter a positive whole number.", OVER_LIMIT: "Amount exceeds the available transfer limit.",
      INSUFFICIENT_PVP_BALANCE: "Not enough PVP funds. Your total balance may include restricted bonuses.",
      INSUFFICIENT_BALANCE: "Not enough website funds.", PLAYER_IN_GAME: "Exit DDZ and wait for game orders to finish before transferring out.",
      INVALID_ARGUMENT: "Transfer rejected. Refresh balances and check the amount.", ORDER_CONFLICT: "Transfer details conflict with the order. Refresh before trying again.",
      ORDER_PENDING: "Transfer result is not confirmed. Check this transfer again.", BALANCE_QUERY_FAILED: "Transfer processed; balances could not be loaded. Refresh and check the existing transfer.",
      STORAGE_UNAVAILABLE: "Enable browser storage to safely track transfers, then reopen this wallet.",
      LAUNCH_FAILED: "Unable to open DDZ. Please try again.",
      INVALID_DDZ_ENTRY: "This game entry is invalid. Choose the game again and retry.",
      GAME_NOT_ALLOWED: "This game is not available for your account. Choose another game or retry.",
      INVALID_SIGNATURE: "Game authorization failed. Please try entering again.",
    },
  },
  id: {
    title: "Dompet DDZ", close: "Tutup", refresh: "Perbarui saldo", retry: "Periksa hasil transfer", amount: "Jumlah transfer",
    unit: "Masukkan bilangan bulat dalam satuan mata uang terkecil. Tanpa desimal.",
    labels: ["Saldo situs", "Tersedia untuk PVP", "Saldo DDZ", "Dapat ditransfer masuk", "Dapat ditransfer keluar"],
    up: "Transfer masuk", down: "Transfer keluar", enter: "Masuk game", loading: "Memuat…", pending: "Sedang diproses. Periksa transfer ini sebelum membuat transfer baru.",
    note: "Masuk game tidak memindahkan saldo. Keluar dari DDZ sebelum transfer keluar. Saldo bonus mungkin dibatasi untuk PVP.",
    done: "Transfer berhasil", unavailable: "Saldo tidak tersedia. Silakan perbarui.",
    errors: {
      INVALID_AMOUNT: "Masukkan bilangan bulat lebih dari nol.", OVER_LIMIT: "Jumlah melebihi batas transfer yang tersedia.",
      INSUFFICIENT_PVP_BALANCE: "Saldo PVP tidak cukup. Saldo total mungkin termasuk bonus yang dibatasi.",
      INSUFFICIENT_BALANCE: "Saldo situs tidak cukup.", PLAYER_IN_GAME: "Keluar dari DDZ dan tunggu transaksi permainan selesai.",
      INVALID_ARGUMENT: "Transfer ditolak. Perbarui saldo dan periksa jumlah.", ORDER_CONFLICT: "Detail transfer tidak cocok. Perbarui sebelum mencoba lagi.",
      ORDER_PENDING: "Hasil transfer belum dikonfirmasi. Periksa transfer ini lagi.", BALANCE_QUERY_FAILED: "Transfer diproses, tetapi saldo gagal dimuat. Perbarui dan periksa transfer ini.",
      STORAGE_UNAVAILABLE: "Aktifkan penyimpanan browser untuk melacak transfer, lalu buka kembali dompet.",
      LAUNCH_FAILED: "Tidak dapat membuka DDZ. Silakan coba lagi.",
      INVALID_DDZ_ENTRY: "Pilihan masuk game tidak valid. Pilih game kembali lalu coba lagi.",
      GAME_NOT_ALLOWED: "Game ini tidak tersedia untuk akun Anda. Pilih game lain atau coba lagi.",
      INVALID_SIGNATURE: "Otorisasi game gagal. Silakan coba masuk lagi.",
    },
  },
  zh: {
    title: "DDZ 上下分", close: "关闭", refresh: "刷新余额", retry: "查询转账结果", amount: "转账金额",
    unit: "按显示币种的最小单位输入正整数，不支持小数。",
    labels: ["网站总余额", "PVP 可用余额", "DDZ 余额", "本次可上分", "本次可下分"],
    up: "上分", down: "下分", enter: "进入游戏", loading: "查询中…", pending: "处理中，请先确认当前转账结果，不能重复创建订单。",
    note: "进入游戏不会自动上下分。下分前请退出 DDZ。总余额中的受限赠送金可能暂不可用于 PVP。",
    done: "转账成功", unavailable: "余额暂不可用，请刷新重试。",
    errors: {
      INVALID_AMOUNT: "请输入大于 0 的整数金额。", OVER_LIMIT: "金额超过本次可上分或可下分上限。",
      INSUFFICIENT_PVP_BALANCE: "PVP 可用余额不足，总余额可能包含受限赠送金。", INSUFFICIENT_BALANCE: "网站总余额不足。",
      PLAYER_IN_GAME: "请先退出 DDZ，并等待局内订单处理完成后再下分。", INVALID_ARGUMENT: "转账参数被拒绝，请刷新余额后检查金额。",
      ORDER_CONFLICT: "订单参数冲突，请刷新后重新操作。", ORDER_PENDING: "转账结果尚未确定，请查询当前转账结果。",
      BALANCE_QUERY_FAILED: "转账已处理，但最新余额查询失败。请刷新余额并确认当前转账结果。",
      STORAGE_UNAVAILABLE: "请启用浏览器存储后重新打开，以便安全保存转账订单。", LAUNCH_FAILED: "获取 DDZ 启动链接失败，请重试。",
      INVALID_DDZ_ENTRY: "游戏入口无效，请重新选择游戏后重试。",
      GAME_NOT_ALLOWED: "当前账号暂未开放该游戏，请选择其他游戏或重试。",
      INVALID_SIGNATURE: "游戏启动授权失败，请重新点击进入游戏。",
    },
  },
};
const fields = ["trunk_balance", "trunk_pvp_available", "ddz_balance", "can_transfer_in", "can_transfer_out"];

const inviteCopy = {
  en: { title: "Room invitation", room: "Room", game: "Game", seats: "Players", expires: "Expires", enter: "Enter room",
    loading: "Checking invitation…", retry: "Check invitation again", note: "Enter room does not transfer funds. Transfer in manually before entering if needed.",
    errors: { INVALID_INVITE: "Invalid invitation link.", INVITE_EXPIRED: "This invitation has expired.", ROOM_UNAVAILABLE: "This room is no longer available or has started.",
      ROOM_FULL: "This room is full.", GAME_NOT_ALLOWED: "This game is not available for your account.", TOKEN_INVALID: "Your login has expired. Please sign in again.",
      INVALID_INVITE_RESPONSE: "Unable to read room details.", INVITE_PREVIEW_FAILED: "Unable to check the invitation. Try again.", NETWORK_ERROR: "Unable to connect. Try again." } },
  id: { title: "Undangan kamar", room: "Kamar", game: "Permainan", seats: "Pemain", expires: "Kedaluwarsa", enter: "Masuk kamar",
    loading: "Memeriksa undangan…", retry: "Periksa undangan lagi", note: "Masuk kamar tidak memindahkan saldo. Transfer masuk secara manual jika diperlukan.",
    errors: { INVALID_INVITE: "Tautan undangan tidak valid.", INVITE_EXPIRED: "Undangan sudah kedaluwarsa.", ROOM_UNAVAILABLE: "Kamar tidak tersedia atau permainan sudah dimulai.",
      ROOM_FULL: "Kamar sudah penuh.", GAME_NOT_ALLOWED: "Permainan tidak tersedia untuk akun ini.", TOKEN_INVALID: "Sesi masuk berakhir. Silakan masuk lagi.",
      INVALID_INVITE_RESPONSE: "Detail kamar tidak dapat dibaca.", INVITE_PREVIEW_FAILED: "Undangan tidak dapat diperiksa. Coba lagi.", NETWORK_ERROR: "Tidak dapat terhubung. Coba lagi." } },
  zh: { title: "房间邀请", room: "房间码", game: "游戏", seats: "人数", expires: "有效期至", enter: "进入房间",
    loading: "正在查询房间邀请…", retry: "重新查询邀请", note: "进入房间不会自动上分；需要时请先手动上分。入房失败后，余额仍在 DDZ，可返回手动下分。",
    errors: { INVALID_INVITE: "邀请链接无效。", INVITE_EXPIRED: "邀请已过期。", ROOM_UNAVAILABLE: "房间已解散、结束或已开局，暂不可进入。",
      ROOM_FULL: "房间已满员。", GAME_NOT_ALLOWED: "该游戏暂未向当前账号开放。", TOKEN_INVALID: "登录已失效，请重新登录。",
      INVALID_INVITE_RESPONSE: "房间信息格式异常，请重试。", INVITE_PREVIEW_FAILED: "查询邀请失败，请重试。", NETWORK_ERROR: "网络连接失败，请重试。" } },
};

export default function DDZWalletEntry({ token, account, language, inviteToken = null, entry = "lobby", autoLaunch = false, onClose, onLaunch, onTransferred }) {
  const [state, setState] = useState(null);
  const controller = useRef(null);
  const callbacks = useRef({ onLaunch, onTransferred });
  callbacks.current = { onLaunch, onTransferred };
  // Supported entries normalize to stable definitions; equivalent prop objects
  // must not recreate a wallet and discard the user's edited transfer amount.
  const normalizedEntry = normalizeMjh5Entry(entry);
  const walletEntry = normalizedEntry === null ? entry : normalizedEntry;
  const t = copy[/^(id|in)/i.test(language) ? "id" : /^zh/i.test(language) ? "zh" : "en"];
  const it = inviteCopy[/^(id|in)/i.test(language) ? "id" : /^zh/i.test(language) ? "zh" : "en"];
  useEffect(() => {
    let storage;
    try { storage = window.localStorage; } catch (_) { /* The controller reports blocked storage. */ }
    const wallet = new DDZWallet({ send: SendCMD, token, account, endpoint: GetHallServerUrl(), storage, inviteToken, entry: walletEntry, onChange: setState });
    controller.current = wallet;
    setState(wallet.state);
    wallet.initialize().then(async () => {
      // Invited players can enter after server preview and wallet checks. Never
      // transfer funds automatically, retry a failed launch, or reuse a Ticket.
      if (!autoLaunch || inviteToken === null || wallet.disposed || wallet.state.inviteBlocked ||
          wallet.state.pending || !wallet.state.invite || !wallet.state.wallet || wallet.state.error) return;
      const url = await wallet.launch();
      if (url && !wallet.disposed) callbacks.current.onLaunch(url);
    });
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { wallet.dispose(); document.body.style.overflow = previous; };
  }, [token, account, inviteToken, walletEntry, autoLaunch]);
  const busy = !state || state.busy;
  const pending = state && state.pending;
  const info = state && state.wallet;
  const invite = state && state.invite;
  const showWallet = inviteToken === null || !!invite;
  const transfer = async (direction, retry = false) => {
    const wallet = controller.current;
    if (!wallet) return;
    await wallet.transfer(direction, retry);
    if (!wallet.disposed && wallet.state.completed && callbacks.current.onTransferred) callbacks.current.onTransferred();
  };
  const launch = async () => {
    const wallet = controller.current;
    if (!wallet) return;
    const url = await wallet.launch();
    if (url && !wallet.disposed) callbacks.current.onLaunch(url);
  };
  return <div class={style.overlay}>
    <section class={style.panel} role="dialog" aria-modal="true" aria-labelledby="ddz-wallet-title" aria-busy={busy}>
      <header><h2 id="ddz-wallet-title">{t.title}</h2><button type="button" disabled={busy} onClick={onClose} aria-label={t.close}>×</button></header>
      {inviteToken !== null && <section class={style.invite} aria-label={it.title}>
        <h3>{it.title}</h3>
        {invite ? <dl>
          <div><dt>{it.room}</dt><dd>{invite.room_code}</dd></div>
          <div><dt>{it.game}</dt><dd>{invite.game_id}</dd></div>
          <div><dt>{it.seats}</dt><dd>{invite.seat_count} / {invite.capacity}</dd></div>
          <div><dt>{it.expires}</dt><dd>{new Date(invite.expires_at * 1000).toLocaleString()}</dd></div>
        </dl> : <p role="status">{state && state.inviteError ? (it.errors[state.inviteError] || it.errors.INVITE_PREVIEW_FAILED) : it.loading}</p>}
        {state && state.inviteLaunchError && <p role="alert">{it.errors[state.inviteLaunchError] || t.errors[state.inviteLaunchError] || it.errors.INVITE_PREVIEW_FAILED}</p>}
        {state && state.inviteError && <button type="button" disabled={busy} onClick={() => controller.current.initialize()}>{it.retry}</button>}
      </section>}
      {showWallet && <div>
      <dl class={style.balances}>{fields.map((field, i) => <div key={field}>
        <dt>{t.labels[i]}</dt><dd>{info ? `${info[field].toLocaleString()} ${info.currency}` : "—"}</dd>
      </div>)}</dl>
      <button class={style.refresh} type="button" disabled={busy} onClick={() => controller.current.query()}>{busy ? t.loading : t.refresh}</button>
      <label class={style.amount} for="ddz-wallet-amount">{t.amount}{info ? ` (${info.currency})` : ""}</label>
      <input id="ddz-wallet-amount" class={style.input} type="text" inputMode="numeric" autoComplete="off" maxLength={16}
        value={state ? state.amount : ""} disabled={busy || !!pending} placeholder="0"
        onInput={event => controller.current.setAmount(event.currentTarget.value)} />
      <p class={style.hint}>{t.unit}</p>
      <div role="status" aria-live="polite" class={style.status}>
        {pending && <p>{t.pending}</p>}
        {state && state.error && state.error !== state.inviteLaunchError && <p>{t.errors[state.error] || it.errors[state.error] || t.unavailable}</p>}
        {state && state.completed && <p>{t.done}</p>}
      </div>
      {pending && <button class={style.retry} type="button" disabled={busy} onClick={() => transfer(pending.direction, true)}>{t.retry}</button>}
      <div class={style.actions}>
        <button type="button" disabled={busy || !!pending || !info || info.can_transfer_in === 0} onClick={() => transfer("UP")}>{t.up}</button>
        <button type="button" disabled={busy || !!pending || !info || info.can_transfer_out === 0} onClick={() => transfer("DOWN")}>{t.down}</button>
      </div>
      <button class={style.enter} type="button" disabled={busy || !!pending || !info || !!(state && state.inviteBlocked)} onClick={launch}>{inviteToken !== null ? it.enter : t.enter}</button>
      {inviteToken !== null && <p class={style.hint}>{it.note}</p>}
      <p class={style.hint}>{t.note}</p>
      </div>}
    </section>
  </div>;
}
